import { QuantityDialog } from '@ordr-as/umami-react/dialog';
import { useCustomisations } from '../../../hooks/useCustomisations';
import { FC, useCallback, useState } from 'react';
import { MenuItemVariantDto } from '../../../../generated-api';
import { sumCartAddition } from '../../../../types/calculation';
import { AddComponentDto, AddItemsDto, CartAdditionOption } from '../../../../types/cart';
import { useLocation } from '../../../context/LocationContext';
import useIntl from '../../../hooks/useIntl';
import { handleMissingCustomizationScroll, isRequiredHandled } from '../../../utils/customizations';
import OptionGroupSection from './customization/OptionGroupSection';
import { useScreenSize } from '../../../context/ScreenSizeContext';
import { useCheckout } from '../../../context/CheckoutContext';
import React from 'react';
import { useCartStore } from '../../../context/cart/CartStore';

interface Props {
  onModalClose: VoidFunction;
  isOpen: boolean;
  item: MenuItemVariantDto;
}

const ItemModal: FC<Props> = ({ isOpen, item, onModalClose }) => {
  const { isPhone } = useScreenSize();
  const { formatCurrency } = useLocation();
  const addMenuItems = useCartStore((x) => x.addMenuItems);
  const intl = useIntl();
  const [options, setOptions] = useState<CartAdditionOption[]>([]);
  const [quantity, setQuantity] = useState(1);
  const { hasAddedAllRequiredOptionGroups, requiredOptionGroups, addedRequiredOptionGroups } = useCustomisations(
    item,
    options
  );

  const { vatType } = useCheckout();

  const handleAddToCart = useCallback(() => {
    if (!hasAddedAllRequiredOptionGroups) {
      handleMissingCustomizationScroll(requiredOptionGroups, addedRequiredOptionGroups);
      return;
    }

    // Build nested components array if this is a composite product
    const components: AddComponentDto[] | undefined =
      item.isComposite && item.components
        ? item.components.map((component) => ({
            itemVariantId: component.productSizeId,
            name: component.productName,
            price: component.effectivePrice,
            takeawayPrice: component.effectiveTakeawayPrice,
            menuSectionId: item.menuSectionId,
            sectionProductId: item.sectionProductId,
            unitQuantity: component.quantity,
            options: [],
          }))
        : undefined;

    const addItem: AddItemsDto = {
      item: {
        itemVariantId: item.variantLinkId,
        name: item.name,
        price: item.price,
        takeawayPrice: item.takeawayPrice,
        menuSectionId: item.menuSectionId,
        sectionProductId: item.sectionProductId,
        courseId: item.courseId,
        components: components,
        options: options.map((option) => {
          return {
            name: option.option.name,
            itemVariantId: option.option.optionId,
            type: option.type,
            quantity: option.quantity,
            price: option.option.price,
            takeawayPrice: option.option.takeawayPrice,
          };
        }),
      },
      quantity: quantity,
    };

    addMenuItems(addItem);
    onModalClose();
  }, [
    addMenuItems,
    addedRequiredOptionGroups,
    hasAddedAllRequiredOptionGroups,
    item.courseId,
    item.menuSectionId,
    item.name,
    item.price,
    item.sectionProductId,
    item.takeawayPrice,
    item.variantLinkId,
    item.isComposite,
    item.components,
    onModalClose,
    options,
    quantity,
    requiredOptionGroups,
  ]);

  if (!isOpen) {
    return null;
  }

  const addItemLabel = `${intl.formatMessage({ id: 'items.addToCartButton' })} (${formatCurrency(
    sumCartAddition({ quantity, item, options, vatType })
  )})`;

  const splitLines = (description?: string): string[] => {
    if (!description) {
      return [''];
    }
    const lines = description.split(/\r?\n/).filter((line) => line.trim().length > 0);

    return lines.length > 0 ? lines : [''];
  };

  return (
    <QuantityDialog
      onClose={onModalClose}
      title={item.name}
      actionLabel={addItemLabel}
      onAction={handleAddToCart}
      isDisabledAction={!hasAddedAllRequiredOptionGroups}
      onQuantityChange={setQuantity}
      size={isPhone ? 'fullwidth' : 'default'}
    >
      <article className="flex flex-col gap-6">
        <section>
          {splitLines(item.description).map((description, index) => (
            <p key={index} className="mb-3">
              {description}
            </p>
          ))}
          <p className="text-grey-dark text-small">{item.allergens.map((a) => a.name).join(', ')}</p>
        </section>
        {item.optionGroups.map((optionGroup) => (
          <OptionGroupSection
            key={optionGroup.variantLinkId}
            optionGroup={optionGroup}
            setOptions={setOptions}
            defaultOptions={[]}
            isRequiredHandled={isRequiredHandled(optionGroup, options)}
          />
        ))}
      </article>
    </QuantityDialog>
  );
};
export default React.memo(ItemModal);
